import { useEffect, useRef, useState } from "react";
import { SendIcon } from "@/components/icons";

const Citations = ({ citations }) => {
  if (!citations?.length) return null;
  return (
    <div className="mt-3 flex flex-col gap-2.5">
      <div className="flex flex-wrap gap-2">
        {citations.map((c, i) => (
          <div
            key={i}
            data-testid="citation-badge"
            className="inline-flex items-center gap-2 h-[30px] px-3 rounded-full border border-foreground bg-white font-mono text-xs"
          >
            <span className="font-medium">{i + 1}</span>
            <span className="truncate max-w-[220px]">{c.doc_name}</span>
          </div>
        ))}
      </div>
      {citations.slice(0, 2).map((c, i) =>
        c.snippet ? (
          <div key={i} className="px-3.5 py-3 rounded-[10px] bg-white text-sm leading-[1.55] text-soft">
            “…<mark>{c.snippet.length > 220 ? `${c.snippet.slice(0, 220)}…` : c.snippet}</mark>”
          </div>
        ) : null
      )}
    </div>
  );
};

export const ChatPanel = ({ chat, hasDocuments, className = "" }) => {
  const { messages, loading, send } = chat;
  const [input, setInput] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const submit = (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    send(text);
  };

  return (
    <div className={`flex flex-col min-h-0 ${className}`}>
      <div
        ref={scrollRef}
        data-testid="chat-messages-list"
        className="flex-1 min-h-0 overflow-y-auto p-4 md:p-6 flex flex-col gap-3 md:gap-[18px]"
      >
        {messages.length === 0 && (
          <div className="m-auto text-center px-6">
            <p className="font-heading text-3xl">Ask a question</p>
            <p className="text-sm text-muted-foreground mt-2">
              {hasDocuments
                ? "Try one your visitors would ask. Answers come only from your sources."
                : "Add a source first, then ask it something."}
            </p>
          </div>
        )}
        {messages.map((m) =>
          m.role === "user" ? (
            <div
              key={m.id}
              className="msg-in paper-dark self-end max-w-[85%] md:max-w-[420px] px-3.5 md:px-[18px] py-2.5 md:py-3 rounded-[16px_16px_4px_16px] text-primary-foreground text-[15px] md:text-base leading-[1.4] whitespace-pre-wrap"
            >
              {m.content}
            </div>
          ) : (
            <div
              key={m.id}
              className="msg-in paper-inset self-start max-w-[92%] md:max-w-[560px] px-3.5 md:px-5 py-3 md:py-4 rounded-[16px_16px_16px_4px] text-[15px] md:text-base leading-[1.55]"
            >
              <p className="whitespace-pre-wrap">{m.content}</p>
              <Citations citations={m.citations} />
            </div>
          )
        )}
        {loading && (
          <div
            data-testid="typing-indicator"
            className="msg-in paper-inset self-start px-5 py-4 rounded-[16px_16px_16px_4px] flex items-center gap-1.5"
          >
            <span className="typing-dot" />
            <span className="typing-dot" />
            <span className="typing-dot" />
          </div>
        )}
      </div>
      <form onSubmit={submit} className="shrink-0 px-3 pb-3 md:px-6 md:pb-6 flex gap-2 md:gap-3">
        <input
          data-testid="chat-input"
          aria-label="Ask a question"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask a question about your documents"
          className="flex-1 min-w-0 h-12 md:h-[52px] px-3.5 md:px-[18px] rounded-xl border border-input bg-white text-base outline-none focus:border-foreground"
        />
        <button
          type="submit"
          data-testid="send-message-button"
          aria-label="Send"
          disabled={!input.trim() || loading}
          className="paper-dark w-12 h-12 md:w-[52px] md:h-[52px] shrink-0 rounded-xl text-primary-foreground flex items-center justify-center"
        >
          <SendIcon size={20} />
        </button>
      </form>
    </div>
  );
};
